import React, { useEffect, useState } from 'react';
import * as IconsaxIcons from 'iconsax-react';
import { Typography } from '@mui/material';
import { iconMap, iconMapKeys } from '../../.icons';
import { IconName, IconProps, IconsaxIconName } from './types';

const Icon: React.FC<IconProps> = ({
  name,
  size,
  className,
  color,
  title = '',
  onClick,
  styles,
  type = 'ICON',
  fill = 'none',
  variant = 'Bold'
}) => {
  const [IconComponent, setIconComponent] = useState<React.ComponentType<any> | null>(null);

  const isEmoji = () => type.toUpperCase() === 'EMOJI';

  const isCustomIcon = () => type.toUpperCase() === 'CUSTOM';

  const isIconsaxIcon = (iconName: string): iconName is IconsaxIconName => {
    return iconName in IconsaxIcons;
  };

  const loadIconComponent = async (iconName: IconName) => {
    if (isEmoji() || isCustomIcon()) return;

    if (isIconsaxIcon(iconName)) {
      setIconComponent(() => IconsaxIcons[iconName]);
      return;
    }

    const dynamicLoader = iconMap[iconName as iconMapKeys];
    if (typeof dynamicLoader === 'function') {
      try {
        const response = await dynamicLoader();
        setIconComponent(() => response.default || response);
      } catch (error) {
        console.error(`Error loading icon: ${iconName}`, error);
      }
    } else {
      console.error(new Error(`Invalid icon name: ${iconName}`));
    }
  };

  useEffect(() => {
    loadIconComponent(name);
  }, [name]);

  const sizeValue = typeof size === 'string' ? size : size ? `${size}px` : undefined;
  const sizeStyles = sizeValue ? { width: sizeValue, height: sizeValue } : undefined;

  if (isEmoji()) {
    return (
      <Typography fontSize={size} className={className} color={color} style={styles} onClick={onClick} role="img">
        {name}
      </Typography>
    );
  }

  if (isCustomIcon()) {
    return (
      <img
        src={name}
        className={className}
        alt={title || ''}
        style={{ ...sizeStyles, ...styles }}
        onClick={onClick}
        role="img"
      />
    );
  }

  if (!IconComponent) return null;

  if (isIconsaxIcon(name)) {
    return (
      <IconComponent
        size={size}
        className={className}
        color={color}
        style={styles}
        variant={variant}
        onClick={onClick}
        role="img"
      />
    );
  }

  return (
    <IconComponent
      fill={fill}
      className={className}
      color={color}
      style={{ ...sizeStyles, ...styles }}
      title={title}
      role="img"
      onClick={onClick}
    />
  );
};

Icon.displayName = 'Icon';

export default Icon;
